<template>
  <div
    class="col-span-full rounded-md bg-ontick-steel-100 p-4 text-ontick-carbon-900 dark:bg-ontick-graphite-500 dark:text-gray-300"
  >
    <div class="">
      <ontick-text size="medium" weight="medium" class="pb-4">
        {{ data.group_name }}
      </ontick-text>
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div
          class="divide-y-2 divide-ontick-steel-200 border-t-2 border-ontick-steel-200 dark:divide-ontick-steel-500 dark:border-ontick-steel-500"
        >
          <div
            v-for="group_param in data.group_params.filter((item) => item.param_name === 'Volume por estratégia')"
            :key="group_param.param_id"
            class="max-h-64 overflow-y-auto"
          >
            <div
              class="my-4 grid grid-cols-1 gap-2 xs:grid-cols-2 xs:gap-0"
              v-for="(sub_param, sub_param_index) in group_param.param_values"
              :key="sub_param.param_id"
            >
              <div class="flex h-full items-center space-x-2">
                <span class="h-3 w-3 rounded-full" :style="`background-color: ${color[sub_param_index]}`"></span>
                <ontick-text class="mr-3 leading-normal" style="white-space: normal">{{
                  sub_param.param_name
                }}</ontick-text>
              </div>

              <ontick-number-input
                v-for="param_subparam in sub_param.param_subparams"
                :key="param_subparam.param_id"
                :class="`sub_param_${param_subparam.param_id}`"
                :value="param_subparam.param_value"
                :min="param_subparam.param_min"
                :max="param_subparam.param_max"
                :step="param_subparam.param_step"
                :normalize="false"
                :disabled="fixedProportion"
                :hideButtons="fixedProportion"
                @input="
                  setParamById({
                    id: param_subparam.param_id,
                    value: Number($event),
                  })
                "
              />
            </div>
          </div>
          <div class="py-4">
            <div class="my-4 grid grid-cols-1 gap-2 xs:grid-cols-2 xs:gap-0">
              <div class="flex h-full items-center">
                <ontick-text class="mr-3 uppercase leading-normal" style="white-space: normal"
                  >Total de contratos</ontick-text
                >
              </div>
              <ontick-number-input
                :value="contractTotal"
                :min="0"
                :max="contractTotalMax"
                :step="contractTotalStep"
                :disabled="!fixedProportion"
                :hideButtons="!fixedProportion"
                :allowManualInput="false"
                :onlyEvents="true"
                @increment-pressed="setContractsProportionally('add')"
                @decrement-pressed="setContractsProportionally('subtract')"
              />
            </div>

            <div
              class="my-4 flex items-start space-x-4"
              v-for="group_param in data.group_params.filter((item) => item.param_type === 'bool')"
              :key="group_param.param_id"
            >
              <input
                type="checkbox"
                id="fixar"
                class="h-4 w-4 mt-0.5 appearance-none rounded border-none bg-ontick-steel-200 checked:bg-ontick-blue-600 focus:ring-ontick-blue-600 focus:checked:bg-ontick-blue-600 dark:bg-ontick-steel-500 dark:checked:bg-ontick-blue-600"
                v-model="fixedProportion"
              />
              <div class="space-y-2">
                <div class="flex">
                  <label for="fixar">
                    <ontick-text class="mr-3 leading-normal" style="white-space: normal">
                      {{ group_param.param_name }}
                    </ontick-text>
                  </label>
                  <form-tooltip :value="group_param.param_tooltip"></form-tooltip>
                </div>
                <p class="text-ontick-yellow-700 text-xs" v-if="!fixedProportion">
                  Obs.: Ao alterar as proporções sugeridas, a performance do seu portfólio será diferente do portfólio
                  original.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="w-full rounded-md bg-gray-50 p-4 text-lg dark:bg-ontick-steel-500">
            <p v-if="contractTotal < info.max_contracts">
              Você ainda possui
              <span class="font-bold text-ontick-yellow-800 dark:text-ontick-yellow-700">
                {{ info.max_contracts - contractTotal }}
              </span>
              contratos para alocar em seu portfólio.
            </p>
            <p v-else-if="contractTotal > info.max_contracts">
              Você ultrapassou o seu limite em
              <span class="font-bold text-ontick-red-500">
                {{ contractTotal - info.max_contracts }}
              </span>
              contratos.
            </p>
            <p v-else>Você está utilizando todos os seus contratos disponíveis.</p>
          </div>
          <v-chart autoresize :option="chartPie(chart)" class="max-h-full max-w-full" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import VChart from "vue-echarts";

export default {
  components: {
    FormTooltip: () => import("../form/form-tooltip.component"),
    Echarts: () => import("../../../../../shared/components/chart/echarts.vue"),
    VChart,
  },
  props: {
    data: {
      type: Object,
    },
  },
  data() {
    return {
      fixedProportion: this.jp.query(this.data, '$..[?(@.param_name=="Fixar proporções")]')[0].param_value,
      breakProportionWarningShown: false,
    };
  },
  computed: {
    //
    //
    // 'this.jp' referente ao pacote 'jsonpath' -> https://www.npmjs.com/package/jsonpath
    //
    //
    ...mapState({
      color: (state) => state.investment.color,
      config: (state) => state.config,
    }),
    ...mapGetters({ info: "investment/getParametersInfo" }),
    strategies() {
      return this.jp.query(this.data, '$..[?(@.param_name=="Quantidade de contratos")]');
    },
    contractTotal() {
      return this.strategies.map((item) => item.param_value).reduce((a, b) => a + b);
    },
    contractTotalStep() {
      return this.strategies.map((item) => item.param_value_original).reduce((a, b) => a + b);
    },
    contractTotalMax() {
      return this.strategies.map((item) => item.param_max).reduce((a, b) => a + b);
    },
    isWithinContractLimit() {
      return this.info.max_contracts >= this.contractTotal;
    },
    chart() {
      return this.strategies.map((item) => {
        return { name: item.strategy_name, value: item.param_value };
      });
    },
  },
  methods: {
    ...mapActions({
      setParamById: "investment/setParamById",
      setSaveIsEnabled: "investment/setSaveIsEnabled",
    }),
    setContractsProportionally(operation) {
      // console.log("click");
      let possibleOperations = ["add", "subtract"];
      if (possibleOperations.includes(operation)) {
        let fields = this.jp.query(this.data, '$..[?(@.param_name=="Quantidade de contratos")]');

        // verifica se a operação não vai ultrapassar os limites
        let safeToIncrement = fields
          .map((item) => item.param_value + item.param_value_original <= item.param_max)
          .every((item) => item === true);
        let safeToDecrement = fields
          .map((item) => item.param_value - item.param_value_original >= item.param_min)
          .every((item) => item === true);
        // let safeToIncrement = safeToIncrementArray.every(
        //   (item) => item === true
        // );
        // let safeToDecrement = safeToDecrementArray.every(
        //   (item) => item === true
        // );

        // determina se incrementa ou decrementa (desde que seja permitido)
        const setValueIfAllowed = (item) => {
          if (operation === "add" && safeToIncrement) {
            return item.param_value + item.param_value_original;
          } else if (operation === "subtract" && safeToDecrement) {
            return item.param_value - item.param_value_original;
          } else {
            return item.param_value;
          }
        };

        // altera o valor de cada item
        for (let field of fields) {
          this.setParamById({
            id: field.param_id,
            value: setValueIfAllowed(field),
          });
        }
      } else {
        console.error("Invalid argument (must be 'add' or 'subtract')");
      }
    },
    resetAllocation() {
      let fields = this.jp.query(this.data, '$..[?(@.param_name=="Quantidade de contratos")]');
      for (let field of fields) {
        this.setParamById({
          id: field.param_id,
          value: field.param_value_original,
        });
      }
    },
    chartPie(value) {
      let dark = this.config.ui_theme == "DARK" ? this.config.theme_chart.dark : this.config.theme_chart.light;

      let color = this.color;
      let option = {
        dataset: {
          dimensions: ["name", "value"],
          source: value,
        },

        grid: {
          top: "5%",
          bottom: "5%",
          left: "0",
          right: "0",
          containLabel: false,
        },
        series: [
          {
            type: "pie",
            radius: ["50%", "75%"],
            selectedMode: false,
            selectedOffset: 8,

            // clockwise: true,
            itemStyle: {
              borderRadius: 5,
              borderColor: dark.line,
              borderWidth: 4,
              color: function (value) {
                return color[value.dataIndex];
              },
            },

            label: {
              show: false,
              position: "center",
            },

            emphasis: {
              label: {
                show: true,
                fontSize: "12",
                fontWeight: "normal",
                textBorderColor: "#fff",
                padding: [3, 8],
                borderRadius: 10,
                formatter: function (params) {
                  return `{a|${params.name}}\n{hr|}\n {b|Volume: ${params.value.value}}  {per|${params.percent}%}`;
                },
                backgroundColor: "rgba(23, 30, 39, 0.9)",
                borderColor: "rgba(23, 30, 39, 1)",
                borderWidth: 0,

                color: "#fff",
                rich: {
                  a: {
                    color: "#fff",
                    lineHeight: 28,
                    align: "center",
                  },
                  hr: {
                    borderColor: "#8C8D8E",
                    width: "100%",
                    borderWidth: 0,
                    height: 0,
                  },
                  b: {
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: "bold",
                    lineHeight: 33,
                  },
                  per: {
                    color: "#fff",
                    backgroundColor: "#0694e6",
                    padding: [3, 4],
                    borderRadius: 4,
                  },
                },
              },
            },
            labelLine: {
              show: true,
            },
          },
        ],
      };
      return option;
    },
  },
  watch: {
    fixedProportion(val) {
      let id = this.jp.query(this.data, '$..[?(@.param_name=="Fixar proporções")]')[0].param_id;
      this.setParamById({ id: id, value: val });
      if (val) {
        this.resetAllocation();
      }
      // else if (!this.breakProportionWarningShown) {
      //   this.breakProportionWarningShown = true;
      //   this.$swal(
      //     "Atenção",
      //     "Alterar as proporções originais pode afetar o desempenho do portfólio.",
      //     "warning"
      //   );
      // }
    },
    isWithinContractLimit: {
      immediate: true,
      handler(val) {
        this.setSaveIsEnabled(val);
      },
    },
  },
  // created() {
  //   console.log(this.data);
  // },
};
</script>

<style></style>
